import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ShoppingBag, ExternalLink } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';

// Minimal brand-consistent wordmarks (same visual language as the Menu delivery strip).
const UberEatsMark = () => (
  <span className="font-bold tracking-tight text-black" style={{ letterSpacing: '-0.02em' }}>
    Uber <span className="text-[hsl(var(--sb-green))]">Eats</span>
  </span>
);
const DeliverooMark = () => (
  <span className="font-bold tracking-tight" style={{ color: '#00CCBC' }}>deliveroo</span>
);
const JustEatMark = () => (
  <span className="font-bold tracking-tight" style={{ color: '#FF8000' }}>Just Eat</span>
);

const PARTNERS = [
  { key: 'uber_eats', label: 'Uber Eats', Mark: UberEatsMark, testId: 'delivery-menu-uber-eats' },
  { key: 'deliveroo', label: 'Deliveroo', Mark: DeliverooMark, testId: 'delivery-menu-deliveroo' },
  { key: 'just_eat', label: 'Just Eat', Mark: JustEatMark, testId: 'delivery-menu-just-eat' },
];

/**
 * "Order for Delivery" CTA + provider popover.
 * URL source of truth: settings.social_links.{uber_eats, deliveroo, just_eat}
 * (Menu page's delivery strip reads from the same fields — no duplicate config.)
 *
 * Props:
 *   variant: 'desktop' | 'mobile-icon' | 'hero-mobile'
 */
export const DeliveryMenu = ({ variant = 'desktop' }) => {
  const { settings } = useSettings();
  const social = settings?.social_links || {};
  const partners = PARTNERS
    .map((p) => ({ ...p, href: social[p.key] }))
    .filter((p) => !!p.href);

  if (!partners.length) return null;

  let trigger;
  if (variant === 'mobile-icon') {
    trigger = (
      <button
        type="button"
        aria-label="Order for delivery"
        data-testid="header-delivery-cta-mobile"
        className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-[hsl(var(--sb-border))] text-white bg-[hsl(var(--sb-surface))] hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sb-gold))] transition-colors"
      >
        <ShoppingBag size={18} />
      </button>
    );
  } else if (variant === 'hero-mobile') {
    // Prominent, full-width mobile CTA rendered at the very top of the Hero.
    trigger = (
      <button
        type="button"
        aria-label="Order for delivery"
        data-testid="hero-delivery-cta-mobile"
        className="w-full inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full bg-white text-black font-semibold shadow-lg shadow-black/40 hover:bg-white/95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sb-gold))] transition-colors text-sm"
      >
        <ShoppingBag size={18} />
        <span>Order for Delivery</span>
      </button>
    );
  } else {
    // Desktop header — white fill, black text (elevated prominence)
    trigger = (
      <button
        type="button"
        aria-label="Order for delivery"
        data-testid="header-delivery-cta"
        className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white text-black font-semibold hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sb-gold))] transition-colors text-sm"
      >
        <ShoppingBag size={16} />
        <span>Order for Delivery</span>
      </button>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        data-testid="delivery-menu-popover"
        className="w-64 p-2 bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white shadow-xl"
      >
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--sb-gold))] font-semibold">
          Choose your platform
        </div>
        <ul className="mt-1 space-y-1">
          {partners.map(({ key, label, href, Mark, testId }) => (
            <li key={key}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testId}
                aria-label={`Order from Smithy Browns on ${label} (opens in a new tab)`}
                className="flex items-center justify-between gap-3 px-3 py-3 rounded-md bg-white hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sb-gold))] transition-colors text-black min-h-[44px]"
              >
                <Mark />
                <ExternalLink size={14} className="text-black/45 shrink-0" aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
